import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { useGatewayPermissions } from "../../auth/permissions";
import { PageHeader } from "../../components/ui";
import { summarizeModelRoutes } from "../models/modelDispatchView";
import { ChannelRuntimeTable, ChannelRuntimeDrawer } from "./ChannelRuntimeView";
import { ModelRuntimeTable, ModelRuntimeDrawer } from "./ModelRuntimeView";
import { CONFIG_REFRESH_INTERVAL, REFRESH_INTERVAL, RuntimeError, RuntimeRefresh, RuntimeSkeleton, usePageVisible } from "./runtimeUi";

export function RuntimePage() {
  const { can } = useGatewayPermissions();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "channels" && can("providers.read") ? "channels" : "models";
  const [modelId, setModelId] = useState<number | null>(null);
  const [channelId, setChannelId] = useState<number | null>(null);
  const visible = usePageVisible();
  const interval = visible ? REFRESH_INTERVAL : false;
  const models = useQuery({
    queryKey: ["models"], queryFn: api.models, enabled: visible,
    refetchInterval: visible ? CONFIG_REFRESH_INTERVAL : false, staleTime: 0,
  });
  const catalog = useQuery({
    queryKey: ["provider-mappings"], queryFn: api.providerMappings, enabled: visible,
    refetchInterval: visible ? CONFIG_REFRESH_INTERVAL : false, staleTime: 0,
  });
  const modelStats = useQuery({
    queryKey: ["runtime", "models"], queryFn: ({ signal }) => api.runtimeModels(signal),
    enabled: tab === "models" && visible, refetchInterval: tab === "models" ? interval : false, retry: false,
  });
  const channelStats = useQuery({
    queryKey: ["runtime", "channels"], queryFn: ({ signal }) => api.runtimeChannels(signal),
    enabled: tab === "channels" && visible, refetchInterval: tab === "channels" ? interval : false, retry: false,
  });
  const modelDetails = useQuery({
    queryKey: ["runtime", "model", modelId], queryFn: ({ signal }) => api.runtimeModel(modelId!, signal),
    enabled: tab === "models" && modelId != null && visible, refetchInterval: modelId != null && tab === "models" ? interval : false, retry: false,
  });
  const channelDetails = useQuery({
    queryKey: ["runtime", "channel", channelId], queryFn: ({ signal }) => api.runtimeChannel(channelId!, signal),
    enabled: tab === "channels" && channelId != null && visible, refetchInterval: channelId != null && tab === "channels" ? interval : false, retry: false,
  });
  const summaries = useMemo(() => summarizeModelRoutes(catalog.data?.data), [catalog.data]);
  const modelById = useMemo(() => new Map(models.data?.data.map((item) => [item.id, item]) || []), [models.data]);
  const channelById = useMemo(() => new Map(catalog.data?.data.channels.map((item) => [item.id, item]) || []), [catalog.data]);
  const current = tab === "models" ? modelStats : channelStats;
  const configurationFetching = models.isFetching || catalog.isFetching;
  const configurationError = models.error || catalog.error;
  const refreshConfiguration = () => {
    void models.refetch();
    void catalog.refetch();
  };
  const refresh = () => {
    void current.refetch();
    refreshConfiguration();
  };
  const changeTab = (value: "models" | "channels") => {
    setModelId(null); setChannelId(null);
    const next = new URLSearchParams(params); next.set("tab", value); setParams(next);
  };
  return <>
    <PageHeader title="运行监控" description="查看模型当前请求状态，以及渠道实时并发占用。" />
    <div className="runtime-page-toolbar">
      <div className="segmented-control" aria-label="运行监控视图">
        <button type="button" aria-pressed={tab === "models"} className={tab === "models" ? "active" : ""} onClick={() => changeTab("models")}>模型使用统计</button>
        {can("providers.read") ? <button type="button" aria-pressed={tab === "channels"} className={tab === "channels" ? "active" : ""} onClick={() => changeTab("channels")}>渠道并发统计</button> : null}
      </div>
      <RuntimeRefresh sampledAt={current.data?.data.sampled_at} fetching={current.isFetching || configurationFetching} error={current.error || configurationError} onRefresh={refresh} />
    </div>
    <RuntimeError error={current.error} retry={() => { void current.refetch(); }} />
    <RuntimeError error={models.error} retry={() => { void models.refetch(); }} />
    <RuntimeError error={catalog.error} retry={() => { void catalog.refetch(); }} />
    {tab === "models" ? <>
      <p className="runtime-scope-note">按请求模型统计当前任务。策略首步直接使用其他模型仍计入生成中；进入后续执行步骤的请求计入已降级。运行中仅计入本次执行未满 20 分钟的请求；其他状态仅计入最近 1 小时内更新的请求。</p>
      {!models.data && models.isPending ? <RuntimeSkeleton /> : models.data ? <ModelRuntimeTable
        models={models.data.data} stats={modelStats.data?.data} pending={modelStats.isPending}
        summaries={summaries} configurationAvailable={Boolean(catalog.data)} onSelect={setModelId}
      /> : null}
    </> : <>
      <p className="runtime-scope-note">按物理渠道的有效并发租约统计。停用但仍有占用的渠道也会保留。</p>
      {!catalog.data && catalog.isPending ? <RuntimeSkeleton /> : catalog.data ? <ChannelRuntimeTable
        catalog={catalog.data.data} stats={channelStats.data?.data} pending={channelStats.isPending} onSelect={setChannelId}
      /> : null}
    </>}
    {modelId != null && tab === "models" ? <ModelRuntimeDrawer
      key={modelId} model={modelById.get(modelId)} channelById={channelById}
      data={modelDetails.data?.data} pending={modelDetails.isPending} fetching={modelDetails.isFetching || configurationFetching} error={modelDetails.error || configurationError}
      refresh={() => { void modelDetails.refetch(); refreshConfiguration(); }} onClose={() => setModelId(null)}
    /> : null}
    {channelId != null && tab === "channels" ? <ChannelRuntimeDrawer
      key={channelId} channel={channelById.get(channelId)} modelById={modelById}
      data={channelDetails.data?.data} pending={channelDetails.isPending} fetching={channelDetails.isFetching || configurationFetching} error={channelDetails.error || configurationError}
      refresh={() => { void channelDetails.refetch(); refreshConfiguration(); }} onClose={() => setChannelId(null)}
    /> : null}
  </>;
}
